export interface VoiceConversationLabels {
  agentSpeaking: string;
  listening: string;
  microphoneOff: string;
  mute: string;
  unmute: string;
  end: string;
  restart: string;
  finish: string;
  start: string;
  retry: string;
  microphonePermissionTitle: string;
  microphonePermissionMessage: string;
  connectionErrorTitle: string;
  connectionErrorMessage: string;
}

export const DEFAULT_VOICE_CONVERSATION_LABELS: VoiceConversationLabels =
  Object.freeze({
    agentSpeaking: 'Agent is talking...',
    listening: 'Agent is listening...',
    microphoneOff: 'Microphone off',
    mute: 'Mute',
    unmute: 'Unmute',
    end: 'End call',
    restart: 'Restart call',
    finish: 'Finish',
    start: 'Start call',
    retry: 'Try again',
    microphonePermissionTitle: 'Microphone access is blocked',
    microphonePermissionMessage:
      'Allow microphone access in your browser settings, then try again.',
    connectionErrorTitle: 'Could not connect',
    connectionErrorMessage: 'Check your connection and try again.',
  });

export type VoiceConversationView =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'postDisconnect'
  | 'error';

interface VoiceConversationViewInput {
  isConnected: boolean;
  isConnecting: boolean;
  hasDisconnected: boolean;
  hasError: boolean;
}

interface PostDisconnectActionsInput {
  hasDisconnected: boolean;
  hasFinishHandler: boolean;
}

interface PostDisconnectRestartInput {
  hasDisconnected: boolean;
  disconnectReason: 'agent' | 'error' | 'user' | null;
}

export function mergeVoiceConversationLabels(
  overrides: Partial<VoiceConversationLabels> = {},
): VoiceConversationLabels {
  return { ...DEFAULT_VOICE_CONVERSATION_LABELS, ...overrides };
}

export function getVoiceConversationView({
  isConnected,
  isConnecting,
  hasDisconnected,
  hasError,
}: VoiceConversationViewInput): VoiceConversationView {
  if (isConnected) return 'connected';
  if (isConnecting) return 'connecting';
  if (hasError) return 'error';
  if (hasDisconnected) return 'postDisconnect';
  return 'idle';
}

export function shouldShowPostDisconnectActions({
  hasDisconnected,
  hasFinishHandler,
}: PostDisconnectActionsInput): boolean {
  return hasDisconnected && hasFinishHandler;
}

export function shouldShowPostDisconnectRestart({
  hasDisconnected,
  disconnectReason,
}: PostDisconnectRestartInput): boolean {
  return hasDisconnected && disconnectReason !== 'agent';
}

export function isMicrophonePermissionError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('name' in error)) return false;

  return ['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(
    String(error.name),
  );
}
